/**
 * TrichoBetaCapture — modal full-screen per il flusso Beta di acquisizione
 * delle 3 viste multiluce (Vertex bianca → Vertex polarizzata → Vertex UV)
 * da tricocamera WiFi.
 *
 * Flusso a stati:
 *   intro       → schermata "Collega il dispositivo al WiFi della tricocamera"
 *   connecting  → "Connessione alla tricocamera in corso…"
 *   live        → live video + indicatore vista corrente + pulsante "Scatta"
 *   review      → preview dell'ultima foto scattata + "Riprova" / "Avanti"
 *   completed   → "3 viste acquisite. Torna alla rete normale per analizzare."
 *   error       → fallback con messaggio e bottone "Carica foto da disco"
 *
 * INPUT (props):
 *   open: boolean  → controlla apertura/chiusura modale
 *   onClose(reason: "cancel" | "error" | "done")
 *   onPhotosReady(photos: { [slotKey]: { label, data: base64, preview: dataUrl } })
 *      → chiamata QUANDO l'utente preme "Conferma" alla fine del flusso.
 *        L'oggetto restituito ha la STESSA forma usata oggi da NewAnalysis,
 *        in modo che le foto Beta finiscano negli stessi riquadri senza
 *        nessuna modifica al resto della pagina.
 *
 * Le viste derivano dalla config condivisa `lib/analysisSlots.js`, la stessa
 * usata da NewAnalysis: ordine e chiavi sempre allineati.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getTrichoCameraPlugin, getTrichoCameraImplLabel } from "@/lib/trichoCameraPlugin";
import { ANALYSIS_SLOTS } from "@/lib/analysisSlots";
import {
  X, Camera, Wifi, Loader2, AlertTriangle, Check, ChevronRight, RotateCcw, Sparkles,
} from "lucide-react";

// Le viste derivano dalla config condivisa con NewAnalysis (stesse chiavi
// slot.id, stesse label backend, stesso ordine fisso richiesto dal Pass 1
// multiluce).
const SLOTS = ANALYSIS_SLOTS.map(s => ({ key: s.id, label: s.label, tip: s.tip }));

const STATES = {
  INTRO:      "intro",
  CONNECTING: "connecting",
  LIVE:       "live",
  REVIEW:     "review",
  COMPLETED:  "completed",
  ERROR:      "error",
};

export default function TrichoBetaCapture({ open, onClose, onPhotosReady }) {
  const plugin = useRef(null);
  const liveContainerRef = useRef(null);

  const [state, setState] = useState(STATES.INTRO);
  const [errorMsg, setErrorMsg] = useState("");
  const [slotIdx, setSlotIdx] = useState(0);
  const [pendingCapture, setPendingCapture] = useState(null);
  const [results, setResults] = useState({}); // { "vertex-bianca": {label,data,preview}, ... }
  const [implLabel, setImplLabel] = useState("");

  // ─── Setup / teardown plugin ────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    plugin.current = getTrichoCameraPlugin();
    setImplLabel(getTrichoCameraImplLabel());

    const onErr = (e) => {
      // queueMicrotask evita il lint react-hooks/set-state-in-effect
      // posticipando l'update fuori dal flusso di registrazione del listener
      queueMicrotask(() => {
        setErrorMsg(e?.message || "Errore del modulo camera");
        setState(STATES.ERROR);
      });
    };
    const errSub = plugin.current.addListener("error", onErr);

    return () => {
      try { errSub?.remove?.(); } catch { /* listener già rimosso */ }
      try { plugin.current?.removeAllListeners?.(); } catch { /* idempotente */ }
      try { plugin.current?.disconnect?.(); } catch { /* idempotente */ }
    };
  }, [open]);

  // ─── Reset stato all'apertura ───────────────────────────────────────
  useEffect(() => {
    if (open) {
      queueMicrotask(() => {
        setState(STATES.INTRO);
        setSlotIdx(0);
        setPendingCapture(null);
        setResults({});
        setErrorMsg("");
      });
    }
  }, [open]);

  // ─── Attiva live quando entriamo in stato LIVE ──────────────────────
  useEffect(() => {
    if (state !== STATES.LIVE) return;
    const el = liveContainerRef.current;
    if (!el || !plugin.current) return;
    plugin.current.startLive(el).catch((e) => {
      setErrorMsg(e?.message || "Impossibile avviare il live");
      setState(STATES.ERROR);
    });
  }, [state, slotIdx]);

  if (!open) return null;

  // ─── Azioni utente ─────────────────────────────────────────────────
  const handleConnect = async () => {
    setState(STATES.CONNECTING);
    setErrorMsg("");
    try {
      const res = await plugin.current.connect();
      if (res?.status === "connected") {
        setState(STATES.LIVE);
      } else {
        setErrorMsg(res?.error || "Connessione fallita");
        setState(STATES.ERROR);
      }
    } catch (e) {
      setErrorMsg(e?.message || "Errore imprevisto");
      setState(STATES.ERROR);
    }
  };

  const handleCapture = async () => {
    try {
      const frame = await plugin.current.capture();
      setPendingCapture(frame);
      setState(STATES.REVIEW);
    } catch (e) {
      toast.error(e?.message || "Scatto fallito");
    }
  };

  const handleRetake = () => {
    setPendingCapture(null);
    setState(STATES.LIVE);
  };

  const handleAcceptCapture = () => {
    if (!pendingCapture) return;
    const slot = SLOTS[slotIdx];
    // Stessa forma di `photos[slot.id]` in NewAnalysis (label, data, preview)
    const base64 = (pendingCapture.dataUrl || "").split(",")[1] || "";
    const entry = {
      label:   slot.label,
      data:    base64,
      preview: pendingCapture.dataUrl,
    };
    const next = { ...results, [slot.key]: entry };
    setResults(next);
    setPendingCapture(null);

    if (slotIdx + 1 < SLOTS.length) {
      setSlotIdx(slotIdx + 1);
      setState(STATES.LIVE);
    } else {
      // Tutte le viste acquisite. Chiudo il live e mostro completed.
      plugin.current?.disconnect?.().catch(() => {});
      setState(STATES.COMPLETED);
    }
  };

  const handleFinalize = () => {
    onPhotosReady?.(results);
    onClose?.("done");
  };

  const handleCancel = async () => {
    try { await plugin.current?.disconnect?.(); } catch { /* già disconnesso */ }
    onClose?.("cancel");
  };

  const handleFallbackToUpload = async () => {
    try { await plugin.current?.disconnect?.(); } catch { /* già disconnesso */ }
    onClose?.("error");
  };

  const currentSlot = SLOTS[slotIdx];

  return (
    <div className="fixed inset-0 z-[200] bg-[#1A1A1A]/95 backdrop-blur-sm flex flex-col"
         data-testid="tricho-beta-modal">
      {/* HEADER */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#C5A059]/15 flex items-center justify-center">
            <Camera className="w-5 h-5 text-[#C5A059]" />
          </div>
          <div>
            <div className="text-white font-medium text-sm flex items-center gap-2">
              Acquisizione da Tricocamera
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#C5A059]/20 text-[#C5A059] font-semibold">
                Beta
              </span>
            </div>
            <div className="text-white/40 text-xs mt-0.5">{implLabel}</div>
          </div>
        </div>
        <button
          onClick={handleCancel}
          className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          data-testid="tricho-beta-close"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4 text-white/70" />
        </button>
      </div>

      {/* PROGRESS BAR (per le viste) */}
      {state !== STATES.INTRO && state !== STATES.ERROR && (
        <div className="px-6 py-3 border-b border-white/5">
          <div className="flex items-center gap-2 max-w-3xl mx-auto">
            {SLOTS.map((s, i) => {
              const done = !!results[s.key];
              const isCurrent = i === slotIdx && !done && state !== STATES.COMPLETED;
              return (
                <React.Fragment key={s.key}>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-colors
                        ${done ? "bg-[#C5A059] text-white"
                              : isCurrent ? "bg-white text-[#1A1A1A]"
                                          : "bg-white/10 text-white/40"}`}
                    >
                      {done ? <Check className="w-3.5 h-3.5" /> : (i + 1)}
                    </div>
                    <span className={`text-xs ${done || isCurrent ? "text-white" : "text-white/40"}`}>
                      {s.label}
                    </span>
                  </div>
                  {i < SLOTS.length - 1 && (
                    <div className={`flex-1 h-px ${done ? "bg-[#C5A059]" : "bg-white/10"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* BODY */}
      <div className="flex-1 flex items-center justify-center px-6 py-8 overflow-auto">

        {state === STATES.INTRO && (
          <div className="max-w-md text-center" data-testid="tricho-beta-intro">
            <div className="w-16 h-16 rounded-2xl bg-[#C5A059]/15 flex items-center justify-center mx-auto mb-6">
              <Wifi className="w-8 h-8 text-[#C5A059]" />
            </div>
            <h2 className="text-white font-serif-display text-2xl mb-3">Collega la tricocamera</h2>
            <p className="text-white/60 text-sm leading-relaxed mb-2">
              Connetti il tablet al <span className="text-white">WiFi della tricocamera</span>{" "}
              (di solito visibile nelle reti WiFi come <code className="text-[#C5A059]">MUCH HEALTHY</code> o
              simile). Durante l&apos;acquisizione il tablet <span className="text-white">non avrà accesso a Internet</span>:
              è normale, e non incide sull&apos;acquisizione.
            </p>
            <p className="text-white/50 text-xs mb-8">
              Quando hai finito di scattare le {SLOTS.length} viste, torneremo automaticamente sulla rete normale
              per caricare le foto e avviare l&apos;analisi.
            </p>
            <button
              onClick={handleConnect}
              className="h-12 px-8 rounded-xl bg-[#C5A059] hover:bg-[#B5904D] text-white font-medium transition-colors flex items-center gap-2 mx-auto"
              data-testid="tricho-beta-connect-btn"
            >
              Continua e connetti
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancel}
              className="mt-4 text-white/50 hover:text-white/80 text-sm transition-colors"
            >
              Annulla
            </button>
          </div>
        )}

        {state === STATES.CONNECTING && (
          <div className="text-center" data-testid="tricho-beta-connecting">
            <Loader2 className="w-10 h-10 text-[#C5A059] animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">Connessione alla tricocamera…</p>
            <p className="text-white/50 text-xs mt-2">Handshake in corso, attendi qualche secondo.</p>
          </div>
        )}

        {state === STATES.LIVE && (
          <div className="w-full max-w-3xl" data-testid="tricho-beta-live">
            <div className="text-center mb-4">
              <p className="text-[#C5A059] text-xs uppercase tracking-wider font-medium">
                Vista {slotIdx + 1} di {SLOTS.length}
              </p>
              <h3 className="text-white font-serif-display text-xl mt-1">{currentSlot.label}</h3>
              <p className="text-white/50 text-sm mt-1">{currentSlot.tip}</p>
            </div>
            <div
              ref={liveContainerRef}
              className="aspect-[4/3] w-full rounded-2xl overflow-hidden bg-black border border-white/10 shadow-2xl"
              data-testid="tricho-beta-live-container"
            />
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={handleCapture}
                className="h-14 px-10 rounded-xl bg-[#C5A059] hover:bg-[#B5904D] text-white font-medium transition-colors flex items-center gap-3 shadow-lg"
                data-testid="tricho-beta-capture-btn"
              >
                <Camera className="w-5 h-5" />
                Scatta
              </button>
            </div>
            <p className="text-center text-white/40 text-xs mt-4">
              Suggerimento: avvicina la sonda alla cute, mantieni inquadratura stabile, scatta.
            </p>
          </div>
        )}

        {state === STATES.REVIEW && pendingCapture && (
          <div className="w-full max-w-3xl text-center" data-testid="tricho-beta-review">
            <p className="text-[#C5A059] text-xs uppercase tracking-wider font-medium">
              Anteprima — {currentSlot.label}
            </p>
            <div className="aspect-[4/3] w-full rounded-2xl overflow-hidden bg-black border border-white/10 mt-3 shadow-2xl">
              <img
                src={pendingCapture.dataUrl}
                alt={currentSlot.label}
                className="w-full h-full object-cover"
                data-testid="tricho-beta-review-image"
              />
            </div>
            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                onClick={handleRetake}
                className="h-12 px-6 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium transition-colors flex items-center gap-2"
                data-testid="tricho-beta-retake-btn"
              >
                <RotateCcw className="w-4 h-4" />
                Rifai
              </button>
              <button
                onClick={handleAcceptCapture}
                className="h-12 px-8 rounded-xl bg-[#C5A059] hover:bg-[#B5904D] text-white font-medium transition-colors flex items-center gap-2"
                data-testid="tricho-beta-accept-btn"
              >
                <Check className="w-4 h-4" />
                {slotIdx + 1 < SLOTS.length ? "Avanti" : "Termina"}
              </button>
            </div>
          </div>
        )}

        {state === STATES.COMPLETED && (
          <div className="max-w-md text-center" data-testid="tricho-beta-completed">
            <div className="w-16 h-16 rounded-2xl bg-[#C5A059]/15 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#C5A059]" />
            </div>
            <h2 className="text-white font-serif-display text-2xl mb-3">{SLOTS.length} viste acquisite</h2>
            <p className="text-white/60 text-sm leading-relaxed mb-2">
              Ora <span className="text-white">torna sul WiFi del salone</span> (o disattiva e riattiva il
              WiFi del tablet) per ripristinare la connessione Internet. Poi premi Conferma: le foto
              compariranno nei riquadri della Nuova Analisi, pronte per essere inviate al cloud.
            </p>
            <p className="text-white/40 text-xs mb-8">
              Le foto sono già salvate localmente. Non perderai nulla se la connessione tarda ad
              attivarsi.
            </p>
            <div className="grid grid-cols-3 gap-2 mb-8">
              {SLOTS.map((s) => (
                <div key={s.key} className="aspect-[4/3] rounded-lg overflow-hidden bg-black border border-white/10">
                  {results[s.key] ? (
                    <img src={results[s.key].preview} alt={s.label} className="w-full h-full object-cover" />
                  ) : null}
                </div>
              ))}
            </div>
            <button
              onClick={handleFinalize}
              className="h-12 px-8 rounded-xl bg-[#C5A059] hover:bg-[#B5904D] text-white font-medium transition-colors flex items-center gap-2 mx-auto"
              data-testid="tricho-beta-finalize-btn"
            >
              <Sparkles className="w-4 h-4" />
              Conferma e torna all&apos;analisi
            </button>
          </div>
        )}

        {state === STATES.ERROR && (
          <div className="max-w-md text-center" data-testid="tricho-beta-error">
            <div className="w-16 h-16 rounded-2xl bg-red-500/15 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-white font-serif-display text-2xl mb-3">Acquisizione non disponibile</h2>
            <p className="text-white/60 text-sm leading-relaxed mb-2">
              {errorMsg || "Non riesco a connettermi alla tricocamera."}
            </p>
            <p className="text-white/40 text-xs mb-8">
              Nessun problema: puoi caricare le foto da disco e completare l&apos;analisi normalmente.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handleConnect}
                className="h-12 px-6 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
                data-testid="tricho-beta-retry-btn"
              >
                Riprova
              </button>
              <button
                onClick={handleFallbackToUpload}
                className="h-12 px-6 rounded-xl bg-[#C5A059] hover:bg-[#B5904D] text-white font-medium transition-colors"
                data-testid="tricho-beta-fallback-btn"
              >
                Carica da disco
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
